import type { DeathStats, HuntSnapshot, OverviewSnapshot } from '@kenocyber/game-domain';
import type {
  ActivePetWire,
  CannonRarity,
  EquipmentTotals,
  LoadoutWire,
  PvpAppearance,
  PvpRuleset,
  ScaledArtifactEffects,
} from '@kenocyber/shared';
import type { OpenWorldCreatureCards, OpenWorldEntityCards } from '../open-world/connection';
import type { PvpClientState, PvpIntent } from '../pvp/connection';
import type { PvpParticipantId } from '../pvp/view';
import type { PetMood } from './pet-mood';

/**
 * Typed, minimal communication channel between React and Phaser.
 *
 * Rules it enforces by construction:
 * - React never reaches into Phaser objects; Phaser never touches React state.
 * - Only the message shapes declared here can cross the boundary.
 * - Phaser NEVER speaks to the network or the database. React owns the API
 *   client; what crosses here is already-authorised state going in, and
 *   presentation state coming out.
 */

export type DevCommand =
  | 'kill-ship'
  | 'toggle-rapid-fire'
  | 'force-bomb'
  | 'force-missile'
  | 'force-poison'
  | 'force-bleeding';

/**
 * Server-authorised progression the scene displays and predicts from.
 *
 * Decimal strings, not numbers: there is no level cap, and the HUD must show
 * what the server actually stored rather than a rounded copy of it.
 */
export interface AuthoritativeProgression {
  readonly level: string;
  readonly xpIntoLevel: string;
  readonly lifetimeXp: string;
  readonly gold: string;
  readonly freePoints: string;
  readonly damagePoints: string;
  readonly healthPoints: string;
  readonly attackSpeedPoints: string;
}

/** Everything the scene needs to run the run the server authorised. */
export interface HuntSessionMessage {
  readonly seed: string;
  readonly mapIndex: number;
  readonly progression: AuthoritativeProgression;
  /**
   * The equipped ship and cannon, as the SERVER resolved them from the
   * account's real inventory. The scene renders and simulates from this: it
   * has no other source of equipment, and nothing in the browser can change
   * it - a modified copy would simply desynchronise from the server's own
   * re-simulation and be corrected on the next checkpoint.
   */
  readonly loadout: LoadoutWire;
  /**
   * The artifact passives the SERVER froze into this session.
   *
   * Same rule as the loadout, and the same reason: the scene simulates with
   * these and has no other source for them. They are exact bigints in fixed
   * integer units - the bridge is in-process, so nothing is serialised and
   * nothing is rounded on the way.
   */
  readonly artifactEffects: ScaledArtifactEffects;
  /**
   * The equipment totals the SERVER froze into this session.
   *
   * Same rule and the same reason as the artifact passives above, and their
   * absence here was a real defect: the scene had no source for what the
   * equipped components were worth, so it simulated with none of them while
   * the server credited the real figure.
   */
  readonly equipmentTotals: EquipmentTotals;
  /**
   * The ACTIVE pet the server froze into this session, or null.
   *
   * Same rule as the loadout and the artifact totals: the scene draws and
   * simulates the pet the server will re-simulate with, and decides none of
   * it. Null is the normal state - a hunt without a pet is complete.
   */
  readonly activePet: ActivePetWire | null;
  /** Validated deterministic state to continue from. */
  readonly snapshot: HuntSnapshot;
}

/** React -> Phaser. */
export interface UiToGameMessages {
  /** Pause/resume the scene simulation (menus, tab switching). */
  'ui:set-paused': { paused: boolean };
  /**
   * (Re)build the simulation from server-authorised state: first bootstrap,
   * a restart, or a new session after a point allocation.
   */
  'hunt:session': HuntSessionMessage;
  /**
   * The server's answer to a checkpoint. Only the progression numbers move -
   * the arena is never recreated by a correction.
   */
  'hunt:reconcile': { progression: AuthoritativeProgression };
  /**
   * The two simulations diverged and the server sent its own state.
   *
   * The scene restores it in place: mobs, boss, projectiles, timers and the
   * RNG all snap to the authoritative run. No canvas, Phaser instance or
   * texture is recreated, and no reward is produced by the restore itself.
   */
  'hunt:correct': { snapshot: HuntSnapshot };
  /**
   * A autoridade executou a ordem de DETONAR (gate V28G.N).
   *
   * ## Porque uma mensagem própria, e não `hunt:correct`
   *
   * Porque uma correção é o que acontece quando as duas simulações DISCORDAM, e
   * aqui elas não discordaram: o jogador pediu uma coisa e o servidor fê-la. A
   * cena adota a instantânea da mesma maneira - sem recriar canvas, textura ou
   * cena - e desenha o efeito, que uma correção nunca deve desenhar.
   *
   * ## Porque o dano viaja aqui
   *
   * Porque na caça o evento `hit` desta detonação nunca chega ao cliente: ela
   * acontece dentro da transação do servidor, que drena a fila de apresentação
   * depois de re-simular. Sem este número o jogador via a barra descer sem um
   * número, e o `CLAUDE.md` exige que cada acerto lógico imprima o seu.
   */
  /**
   * **A autoridade respondeu a uma ORDEM de carta** (gate V28H.I).
   *
   * Publicada aceite OU recusada, e a razão é a mesma nos dois casos: a
   * instantânea que vem com ela é a verdade, e a cena adopta-a. Numa recusa ela
   * traz a cauda liquidada e o estado real dos espaços - que é como uma barra
   * que discordava da autoridade se corrige.
   *
   * A cena não decide nada com `slot` e `accepted`: eles existem para o efeito
   * de apresentação saber que houve uma ordem e qual, sem ter de comparar duas
   * instantâneas para descobrir.
   */
  'hunt:card-activated': {
    readonly snapshot: HuntSnapshot;
    readonly slot: string;
    readonly accepted: boolean;
  };
  'hunt:bleed-detonated': {
    /** O estado autoritativo depois da ordem. Adotado no lugar. */
    snapshot: HuntSnapshot;
    /** O que rebentou, por alvo. Vazio quando não havia marcas. */
    detonated: readonly {
      readonly targetId: number;
      /** Quantas marcas rebentaram. Só a intensidade do efeito. */
      readonly marks: number;
      /** O dano REALMENTE aplicado, depois do clamp. O número impresso. */
      readonly applied: number;
    }[];
  };
  /**
   * Points were spent and the server applied them to the RUNNING hunt.
   *
   * Deliberately not `hunt:session`: this is the same session, the same wave
   * and the same enemies, with different numbers on the ship. The scene
   * reconciles its simulation and lets the existing sprites follow their
   * entities by id - it does not clear the arena, rebuild the cannons, restart
   * the boss countdown or announce a restart.
   */
  'hunt:reconfigure': {
    /**
     * The authoritative run, or `null` when there is no authority behind it.
     *
     * Same rule as `hunt:pet`, and the same reason. The server settles the
     * tail before answering only when the session is ECONOMIC; a debug session
     * is refused at checkpoint by design, so nothing ever advances the snapshot
     * on its row and it still describes the instant the session was created.
     * Restoring from that takes the arena back there - mobs gone, boss
     * countdown rolled up, kill counter reset, simulated time REDUCED.
     *
     * So it travels only when it is true. Without it the scene applies the new
     * numbers to the run it is already drawing, through `applyLoadout`, which
     * makes exactly the promise this message has always made: shots in the air
     * keep the damage they were born with, the accumulator is not reset, and
     * health follows the damage already taken.
     */
    snapshot: HuntSnapshot | null;
    /**
     * Present when the change was an EQUIPMENT swap rather than a point spend.
     *
     * The ship art, the cannon art and the formation all follow it. Shots
     * already in the air are untouched: their damage lives in the snapshot,
     * which the server produced with them intact.
     */
    loadout?: LoadoutWire;
    /**
     * Present when the change was an ARTIFACT passive: a copy dropped, or two
     * were merged.
     *
     * Nothing visual follows it - artifacts are not drawn on the ship - and
     * nothing about the arena moves: same wave, same boss, same countdown,
     * same shots in the air with the damage they were born with. Only volleys
     * fired after this message use the new numbers.
     */
    artifactEffects?: ScaledArtifactEffects;
  };
  /**
   * The account's artifact passives changed, and apply FROM NOW ON.
   *
   * Deliberately not `hunt:reconfigure`: there is no new snapshot to adopt.
   * A drop confirmed by a checkpoint changes what the ship fights with and
   * nothing else, so the scene hands the new totals to the simulation it is
   * already running. Mobs, boss, countdown, spawn schedule, RNG streams,
   * metrics and every projectile in flight are untouched - each shot keeps the
   * damage it was born with, and only later volleys use the new numbers.
   */
  'hunt:artifacts': { artifactEffects: ScaledArtifactEffects };
  /**
   * The active pet changed: activated, deactivated or evolved.
   *
   * Deliberately not `hunt:session`: this is the same session, the same wave
   * and the same enemies, with a different escort. The scene swaps the model
   * and the muzzle count and leaves everything else exactly where it is -
   * mobs, boss countdown, RNG streams, metrics, and every bullet in the air
   * with the damage it was born with.
   *
   * `snapshot` is null when the session is NON-ECONOMIC, and that is what
   * makes the sentence above true rather than aspirational. A debug session
   * never checkpoints, so the server never settles its tail and the snapshot
   * it holds stays at the state the session was created in: answering an
   * evolution with it hands the scene a run six seconds younger than the one
   * on screen, and the mobs, the countdown and the kills go back with it. The
   * server is not wrong to keep that stub - it credits nothing and tracks
   * nothing - so the client simply does not adopt what was never authoritative
   * and applies the pet in place, exactly as `hunt:dev-allocate` does with
   * points. An economic session still adopts: there the snapshot was settled
   * to `now` inside the same transaction and IS the truth.
   */
  'hunt:pet': { activePet: ActivePetWire | null; snapshot: HuntSnapshot | null };
  /**
   * The active pet's TRAINING or APPEARANCE changed.
   *
   * Deliberately not : no snapshot travels, because nothing about
   * the simulation moved. The same pet is flying, in the same run, with the
   * same mobs and the same boss countdown - it is wearing a different colour,
   * or it hits slightly harder from the next volley on. The scene hands the
   * new state to the renderer and changes nothing else.
   */
  'hunt:pet-appearance': { activePet: ActivePetWire | null };
  /**
   * DEV/E2E ONLY - which representation of the pet to draw.
   *
   * A presentation switch and nothing else: damage, cadence, targeting,
   * evolution and rewards are identical in both modes, because both draw the
   * SAME simulation. It exists so the two can be compared side by side in one
   * session before the launch representation is chosen; production is 2D.
   */
  'hunt:pet-render': { mode: 'pet-2d' | 'pet-3d' };
  /** Return to the hunt after death. */
  /**
   * The mining ring, as the SERVER last described it.
   *
   * Sent whenever an authoritative answer lands, and at no other time. The
   * scene lays the ring out from this list and nothing else - it never counts
   * beams to work out a fleet, and the fleet it draws can only be the one the
   * server confirmed.
   *
   * An empty list means the tab is closed or nothing is docked, and the scene
   * clears its objects. That is what "closing removes only the mining objects"
   * is: a message, not a scene teardown.
   */
  'mining:state': {
    readonly ships: readonly {
      readonly instanceId: string;
      readonly shipIndex: number;
      readonly assetKey: string;
      readonly power: string;
    }[];
    readonly totalPower: string;
    /**
     * The planet the fleet is breaking, exactly as the SERVER described it.
     *
     * Presentation reads this and decides nothing: the health, the maximum,
     * the tier and the scale are all the server's, and the scene draws a
     * smaller sphere because the number arrived smaller - never the other way
     * round. Null before the first answer lands.
     */
    readonly planet: {
      readonly tier: number;
      readonly remainingHealth: string;
      readonly maxHealth: string;
      readonly visualScaleBps: number;
      readonly maxStageFractionBps: number;
      readonly palette: string;
    } | null;
  };
  /** Which docked ship the panel has highlighted, or null. */
  'mining:select': { readonly instanceId: string | null };
  /** Whether the ring is on screen at all. */
  'mining:visible': { readonly visible: boolean };
  /**
   * Where inside the canvas the modal has left a hole for the rings.
   *
   * The mining window is ONE window: its controls are DOM and its stage is a
   * transparent rectangle in the middle of them, through which the arena's own
   * canvas shows. That is what keeps this to one canvas and one renderer - the
   * alternative was a second canvas parented to the modal, which is a second
   * WebGL context and a second thing to lose.
   *
   * Canvas pixels, measured from the canvas element's own top-left. The scene
   * runs its camera at 1:1 so these arrive already in the coordinates it works
   * in, and nothing has to be converted twice.
   */
  'mining:stage': {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  'hunt:restart': Record<string, never>;
  /** DEV ONLY - ignored unless the scene was created with debug enabled. */
  'hunt:dev': { command: DevCommand };
  /**
   * DEV ONLY - spends points inside a NON-ECONOMIC test session.
   *
   * A debug session never checkpoints, so the server holds no points to spend
   * and a real allocation would be refused. This keeps the panel usable for
   * testing while persisting nothing: inert unless the build allows tooling
   * AND the scene is in debug mode.
   */
  'hunt:dev-allocate': { damage: bigint; health: bigint; speed: bigint };
  /**
   * DEV ONLY - swaps the equipped cannon to test formations. Restarts the test
   * session, which the dev panel states plainly.
   */
  'hunt:dev-cannon': { rarity: CannonRarity; level: number };
  /**
   * DEV/E2E ONLY - where the 3D model's machine guns actually are.
   *
   * In 3D the pet is a real object that banks and rotates, so the mouth of a
   * gun is nowhere near the fixed offset the simulation uses for the sprite.
   * The overlay projects each `muzzle_n` hardpoint's world position into
   * arena coordinates and sends them here, so the flash is drawn at the gun
   * that fired rather than at where a sprite's gun would have been.
   *
   * Presentation only, in both directions: the simulation decides where the
   * bullets are, and these coordinates never reach it.
   */
  'hunt:pet-muzzles': { origins: readonly { x: number; y: number }[] };
  /**
   * Where the 3D model's EMITTER actually is, in arena coordinates.
   *
   * The overlay's answer to `hunt:pet-transform`, sent from the same frame that
   * drew it. A beam has to start at the crystal the player can SEE, and that is
   * not the simulation's pet position: the model is drawn at a smoothed
   * position pursuing the authoritative one, plus a vertical bob, so anchoring
   * the beam to `pet.x/y` left it emerging from her flank whenever she moved.
   *
   * Null when no model is loaded, and the scene falls back to the simulation's
   * own position - a beam one frame out of place beats no beam at all.
   */
  'hunt:pet-emitter': { origin: { x: number; y: number } | null };

  /**
   * Whether the hunt is drawn at all.
   *
   * Pausing and hiding are two different things, and a duel needs both. The
   * pause stops the hunt's CLOCK - it grants no progress and its ship stops
   * moving - but the scene goes on drawing its last frame, and the two scenes
   * share one canvas. A player would see a duel layered over a frozen hunt.
   *
   * So the hunt hides itself the way the mining ring already hides its ring:
   * the scene, the canvas, the renderer and every object survive, and the
   * simulation goes on accepting authoritative bootstraps while invisible.
   * Nothing here destroys, resets or forgets anything.
   */
  'hunt:visible': { readonly visible: boolean };

  /* --- Duels. Three messages in, and not one carries a credential. --- */

  /**
   * Open the duel view, as the seat the SERVER assigned.
   *
   * Passed rather than inferred so the scene can tell "mine" from "theirs"
   * before a single frame has arrived. It comes out of the welcome, so it is
   * the server's answer and never a choice this client made.
   *
   * Like the mining ring, this is a MESSAGE and not a scene start: the scene is
   * active from boot and draws nothing until told to, so there is one canvas
   * and one WebGL context for the life of the tab.
   */
  'pvp:open': {
    readonly seat: PvpParticipantId;
    /**
     * Which shape of match this is: `duel_1v1` or `team_3v3`.
     *
     * Carried rather than inferred, and it is worth saying why it cannot be
     * inferred. The scene needs it to choose which VFX profile a skin compiles
     * with - `pvp1x1` draws the full signature, `pvp3x3` halves the cadence and
     * the ceilings because six ships cost more than two - and it needs the
     * answer at OPEN, before any ship has arrived. Counting hulls would answer
     * "a duel" for a team match whose other five sockets have not connected
     * yet, and then quietly change its mind as they did, rebuilding every
     * renderer mid-fill. The roster is a thing that fills; the ruleset is a
     * thing the server decided before anybody was invited.
     *
     * It says nothing about the simulation. The scene renders whatever the
     * authority sends either way, and no rule, number or hitbox in this file's
     * consumers reads it.
     */
    readonly ruleset: PvpRuleset;
  };
  /**
   * The HUD asking the arena to open its target mode.
   *
   * A message rather than a shared object, like every other thing React says
   * to Phaser: the button lives in React, the cursor and the ring live in the
   * scene, and neither reaches into the other. Arming grants nothing - what
   * eventually crosses the socket is one point, once, and the room decides it.
   */
  'pvp:arm-teleport': Record<string, never>;
  /**
   * O jogador carregou em DETONAR (gate V28G.N).
   *
   * React é dono do botão; Phaser é dono do frame de comando que o leva. Nenhum
   * chega ao outro, que é a mesma regra que a afordância do teleporte segue.
   *
   * Sem payload, e não é um esquecimento: não há alvo, contagem nem magnitude que
   * caiba aqui. A ordem é "agora", e o resto é da autoridade.
   */
  'pvp:detonate-bleed': Record<string, never>;
  /**
   * **A ORDEM de ligar uma carta, do HUD para a cena** (gate V28H.J).
   *
   * React é dono da barra; Phaser é dono do frame de comando que a leva. A
   * mesma fronteira da detonação e do teleporte, e pela mesma razão: nenhum dos
   * dois chega ao outro, e nada aqui guarda estado - a ordem é o próprio
   * clique, ou a própria tecla.
   *
   * A identidade da ordem nasce no HUD e viaja aqui: é ela que faz a sala
   * reconhecer uma repetição em vez de ligar a carta uma segunda vez.
   */
  'pvp:activate-card': { readonly slot: 1 | 2 | 3; readonly commandId: string };
  /** A ordem de SOLTAR uma carga, pela mesma ponte (gate V28H.N). */
  'pvp:release-card': { readonly slot: 1 | 2 | 3; readonly commandId: string };
  /**
   * **A ordem de PILOTAR** o que este casco está a tocar (gate V28H.X).
   *
   * Sem espaço e sem alvo: ela só tem a identidade da ordem. Quem está a tocar
   * o quê é uma pergunta da SALA, e o botão que a manda não sabe a resposta -
   * ele só sabe que o jogador carregou.
   */
  'pvp:board-summon': { readonly commandId: string };
  /**
   * The authoritative world, whole.
   *
   * ONE message rather than five, because the client state already carries the
   * arena from the welcome, the latest frame, the correction counter, the
   * connection phase and the result - and a payload that re-derived any of them
   * would be a second copy to keep in step with the first. The scene feeds this
   * to its own `PvpViewBuffer` and samples per frame, so no rendering cadence
   * ever travels over the bridge.
   *
   * A `correction` higher than the last one means the world was REPLACED rather
   * than advanced. The scene drops trails, interpolation and every projectile
   * that is not in the new snapshot, immediately.
   */
  'pvp:authoritative': { readonly state: PvpClientState };
  /**
   * Os cortes CONFIRMADOS desde a mensagem anterior.
   *
   * Separado de `pvp:authoritative` de propósito. Aquele descreve um ESTADO -
   * onde tudo está agora - e é reenviado a cada frame do servidor; um
   * acontecimento colado a ele seria redesenhado sempre que o estado voltasse
   * a passar. Isto é uma lista de factos, entregue uma vez.
   *
   * Carrega o efeito e onde, e mais nada: nem magnitude, nem nível, nem
   * identificador de posse, nem parâmetro de arte. A cena escolhe o desenho.
   */
  'pvp:proc': {
    readonly cuts: readonly {
      readonly id: number;
      readonly procId: string;
      /**
       * Quem foi curado, e o tique em que o facto aconteceu (gate V28G.I).
       *
       * A chave visual do Roubo de Vida é `(atacante, tique)`: o jogador quer
       * saber quanto a nave recuperou, e não de qual alvo cada fração saiu. O
       * alvo não vem por isso mesmo - ele não entra em decisão nenhuma deste
       * lado, e um campo que ninguém lê é um campo que alguém acaba por ler.
       */
      readonly attacker: string;
      /**
       * **Em QUEM o efeito foi confirmado** (gate V28G.U).
       *
       * Autoritativo, e não uma escolha da cena. Entrou quando o canal passou a
       * carregar um efeito cujo SENTIDO é o inverso: no Roubo de Vida o curado é
       * o `attacker` e o alvo era drenado; numa cura de suporte quem ativa é o
       * `attacker` e quem recebe é este - e o número verde tem de aparecer sobre
       * a nave certa das duas.
       */
      readonly target: string;
      readonly tick: number;
      /**
       * A vida REALMENTE acrescentada, em milli inteiro. Ausente se não cura.
       *
       * Autoritativa e nunca recalculada: o renderer não tem perfil, nível nem
       * percentagem, e a diferença observada na barra move-se também por dano.
       */
      readonly healedMilli?: number | undefined;
      readonly x: number;
      readonly y: number;
    }[];
  };
  /**
   * As EXPLOSÕES confirmadas desde a mensagem anterior (gate V28G.J).
   *
   * Uma mensagem própria e não um campo em `pvp:proc`, porque são factos de
   * formas diferentes: um proc acontece SOBRE um alvo, e uma explosão acontece
   * NUM SÍTIO, com um raio e uma lista de quem lá estava. Uma bomba que detona
   * contra uma parede não tem alvo nenhum, e um `targetId` inventado seria uma
   * mentira que a cena desenharia.
   *
   * Carrega o efeito, o centro, o raio e quem levou - e mais nada: nem
   * magnitude, nem nível, nem joia, nem parâmetro de arte. A cena escolhe o
   * desenho; a autoridade escolhe o que aconteceu.
   */
  'pvp:blast': {
    readonly blasts: readonly {
      /** O projétil que detonou. A chave de deduplicação. */
      readonly id: number;
      /** O EFEITO confirmado, nunca a joia. */
      readonly effectId: string;
      readonly attacker: string;
      readonly tick: number;
      readonly x: number;
      readonly y: number;
      /**
       * O raio MECÂNICO, em unidades de mundo.
       *
       * A onda de choque desenhada representa ESTE número. Uma cena com o seu
       * próprio raio teria uma segunda cópia dele, e o dia em que uma das duas
       * mudasse seria o dia em que o círculo passava a mentir sobre quem levou.
       */
      readonly radius: number;
      /** Quem levou dano, para a cena piscar exatamente esses cascos. */
      readonly hit: readonly string[];
    }[];
  };
  /**
   * As DETONAÇÕES de sangramento confirmadas desde a mensagem anterior
   * (gate V28G.N).
   *
   * ## Uma mensagem própria, e a terceira do género
   *
   * Porque o facto tem uma forma que nenhuma das outras duas tem. Um proc
   * acontece sobre um alvo e deduplica pelo PROJÉTIL que o trouxe - uma
   * detonação não tem projétil, porque a marca sobreviveu ao tiro que a semeou.
   * Uma explosão acontece num SÍTIO, com raio - uma detonação não tem raio, não
   * tem área, e acontece sobre cascos.
   *
   * ## E porque uma cena precisa dela, quando as marcas já são estado
   *
   * Porque as marcas desaparecerem é AMBÍGUO: elas saem quando rebentam, quando
   * o alvo morre e quando o autor morre. Uma cena que inferisse a detonação de
   * "a lista encolheu" acenderia o corte carmesim sobre um casco que apenas foi
   * abatido. Isto é a autoridade a dizer qual das três aconteceu.
   *
   * Carrega quem, sobre quem e quantas - e mais nada: nem magnitude, nem dano,
   * nem nível, nem joia, nem parâmetro de arte. O dano já viaja como vida a
   * descer nos frames de estado; a cena escolhe o desenho.
   */
  'pvp:bleed': {
    readonly bursts: readonly {
      /** `(autor, tique, alvo)`, já deduplicada pela ligação. */
      readonly key: string;
      /** Quem mandou. Em 3x3, é o que separa as marcas próprias das de um aliado. */
      readonly attacker: string;
      /** Em quem rebentou. */
      readonly target: string;
      /** Quantas marcas rebentaram. Decide quantas garras, até ao tecto. */
      readonly marks: number;
      /**
       * O dano REALMENTE aplicado, em pontos inteiros.
       *
       * Autoritativo e nunca recalculado: o cliente não tem as magnitudes
       * congeladas de cada marca, e a barra desce também por tiros do mesmo
       * tique. Zero é possível - um alvo já sem vida - e não imprime número.
       */
      readonly damage: number;
    }[];
  };
  /**
   * **Uma REFLEXÃO que a autoridade confirmou** (gate V28G.O).
   *
   * ## Uma mensagem própria, e a quarta do género
   *
   * Porque o facto tem uma forma que nenhuma das outras três tem, e o que a
   * separa é uma coisa concreta: **duas posições**. Um proc acontece sobre um
   * alvo; uma explosão acontece num sítio; uma detonação acontece sobre cascos.
   * Uma reflexão acontece ENTRE dois - o brilho corre na silhueta de quem
   * defendeu, e o feixe acaba em quem recebe o payload virado.
   *
   * As posições viajam no evento e não são lidas do estado, e a razão é de
   * temporização: elas são do tique da INTERCEÇÃO, e um frame carrega mais do
   * que um tique. Lê-las ao desenhar daria as posições da publicação, e o feixe
   * ligaria dois pontos onde nada aconteceu.
   *
   * ## Dois estágios, e nenhum é derivável do outro
   *
   * `i` acende o brilho e - só para um tiro comum - desenha o feixe. `l` imprime
   * `REFLECT` sobre o número. Para o Míssil e para a Bomba o segundo chega
   * segundos depois do primeiro, e um payload que vá bater numa parede nunca o
   * produz.
   *
   * Carrega quem, sobre quem, que tipo de payload, o estágio, as duas posições e
   * o dano - e mais nada: nem magnitude congelada, nem nível, nem joia, nem
   * parâmetro de arte. `payload` é o TIPO de efeito intercetado e nunca a peça
   * que o emitiu.
   */
  'pvp:reflect': {
    readonly reflects: readonly {
      /** `(refletor, destinatário, tique, payload, estágio)`, já deduplicada. */
      readonly key: string;
      /** O REFLETOR: onde o brilho corre, e o novo autor do payload. */
      readonly reflector: string;
      /** O destinatário: onde o feixe acaba, e onde o número é impresso. */
      readonly target: string;
      /**
       * Que instância hostil foi intercetada.
       *
       * Opaca para a cena, tirando uma comparação: `cannon_shot` é o único
       * payload que desenha um feixe, porque é o único cujo redirecionamento não
       * tem entidade nenhuma a viajar. Nunca o nome de uma joia.
       */
      readonly payload: string;
      /** `i` acende o brilho; `l` imprime o número. */
      readonly stage: 'i' | 'l';
      /**
       * O refletor, em MILLI-unidades, no tique da interceção.
       *
       * Como todas as posições autoritativas que atravessam a ponte. A cena
       * converte-as com `pvpWorldUnits` antes de desenhar - dizer aqui que já
       * eram unidades de mundo foi o que fez o feixe e o número serem desenhados
       * mil vezes fora da arena (gate V28G.O.2).
       */
      readonly x: number;
      readonly y: number;
      /** O destinatário, no mesmo tique. */
      readonly tx: number;
      readonly ty: number;
      /**
       * O dano REALMENTE aplicado, em MILLI-vida, ou `null` em `i`.
       *
       * Milli, como a vida viaja em todo o lado. Dizer aqui "pontos inteiros"
       * fez o `REFLECT` imprimir mil vezes o golpe - o mesmo erro de unidade
       * das coordenadas ao lado, e escondido pela mesma razão (gate V28G.O.2).
       *
       * Autoritativo e nunca recalculado: o cliente não tem as magnitudes
       * congeladas, e a barra desce também por tiros do mesmo tique.
       */
      readonly damage: number | null;
    }[];
  };
  /**
   * The sequence the connection gave an intent the scene published.
   *
   * The scene predicts its own ship by replaying every intent the room has not
   * acknowledged yet, and "not acknowledged" is a comparison against `ack` -
   * which means it needs the number. It cannot infer it: the connection drops
   * an intent that arrives while the room is not running, so a counter kept on
   * this side would drift by exactly the intents that were never sent.
   *
   * Presentation only. It carries no position, no damage and no authority; the
   * room's frame remains the sole truth about where anything is.
   */
  'pvp:intent-sent': { readonly seq: number; readonly intent: PvpIntent };
  /**
   * Close the duel view and let go of everything it made.
   *
   * Objects, input handlers, camera bounds and timers. The scene, the canvas
   * and the renderer all survive - and so does the hunt, which React resumes
   * through its own existing path rather than through anything here.
   */
  'pvp:close': Record<string, never>;
  /**
   * **Abre o quadrante.** React manda; Phaser desenha.
   *
   * O que atravessa é o mínimo para a cena existir: o manifesto já é dela, e o
   * resto — quem está lá, onde, com que vida — chega frame a frame por
   * `open-world:frame`. Nada de carga, nada de ouro e nada de bilhete: a cena
   * não tem nada que fazer com nenhuma dessas coisas.
   */
  'open-world:open': Record<string, never>;
  /** Fecha o quadrante e larga tudo o que a cena criou. */
  'open-world:close': Record<string, never>;
  /**
   * **Os comandos locais ficam de parte enquanto uma camada está aberta**
   * (gate V28I.R).
   *
   * ## O que ela NÃO faz
   *
   * Não pausa nada. A sala continua a correr, os outros continuam a mover-se,
   * o combate continua, os tempos de espera correm, os mobs e os bosses
   * continuam vivos, as caixas continuam a expirar — e a nave continua
   * **vulnerável**. Abrir um mapa nunca foi uma forma de ficar a salvo.
   *
   * ## O que ela faz
   *
   * Larga o que estava a ser segurado e passa a ignorar o que for premido.
   * Sem isto, uma tecla que já estava em baixo quando o mapa abriu continuava
   * a mandar a nave contra a fronteira enquanto o jogador lia a grelha.
   *
   * O comando continua a sair pelo caminho normal, todos os tiques, apenas
   * **neutro**: `mx` e `my` a zero e sem disparo. A autoridade não sabe que
   * há um mapa aberto, e é assim que se garante que ele não vale nada.
   *
   * Ao reativar, larga tudo outra vez: uma tecla presa antes de abrir não
   * volta a valer ao fechar.
   */
  'open-world:controls': { readonly enabled: boolean };
  /**
   * **O último frame autoritativo**, empurrado tal como chegou.
   *
   * Uma fotografia, substituída por inteiro. A cena interpola entre esta e a
   * anterior e não guarda uma sua: não há simulação deste lado, e portanto não
   * há nada que possa discordar do servidor.
   */
  'open-world:frame': {
    readonly me: number;
    readonly sideMu: number;
    readonly entities: readonly {
      readonly id: number;
      readonly x: number;
      readonly y: number;
      readonly aimX: number;
      readonly aimY: number;
      /** A intenção de movimento que a autoridade aplicou (gate V28I.BX.2). */
      readonly moveX?: number;
      readonly moveY?: number;
      readonly healthMilli: string;
      readonly maxHealthMilli: string;
      readonly radiusMu: number;
      readonly appearance: PvpAppearance | null;
      readonly connected: boolean;
      /** Esta nave voa comigo, segundo a autoridade. Nunca uma cor do navegador. */
      readonly ally: boolean;
      /**
       * **O que as cartas estão a fazer a este casco, e a desenhar nele.**
       *
       * O que a autoridade disse, sem uma única derivação. Nunca quanto falta de
       * nada: isso é informação de decisão, e só existe para o próprio piloto.
       */
      readonly cards: OpenWorldEntityCards;
    }[];
    /**
     * **Os corpos em voo, separados entre o que a sala disse e o que se desenha**
     * (gate V28I.AK).
     *
     * `base` é autoritativo e imutável desde o nascimento; `x` e `y` são a
     * posição derivada, movida em pé pela camada de apresentação da ligação. A
     * separação existe porque um corpo linear muda de sítio a cada trama sem que
     * nada da base mude — juntá-los obrigava a reconstruir o objeto inteiro só
     * para mover dois números, uma vez por corpo e por trama.
     *
     * A cena **lê** as duas metades e não escreve em nenhuma.
     */
    readonly shots: readonly {
      readonly base: {
        readonly id: number;
        /**
         * **Que corpo de carta este projétil é**, ou nada.
         *
         * `f` a Esfera Ígnea, `c` o Catolé, `z` a esfera do Congelamento, `t` a
         * estrela da Lentidão e `s` o raio do Curto-Circuito. A cena não o pode
         * deduzir: as cinco coisas desenham-se de maneiras diferentes.
         */
        readonly card: 'f' | 'c' | 'z' | 't' | 's' | 'w' | 'g' | 'd' | null;
        /** O raio da explosão que ele vai fazer. Só com `card`, e só se rebenta. */
        readonly blastMu: number | null;
        /** Quem o disparou, pela identidade de entidade da sala (gate V28I.CF.3). */
        readonly owner: number;
        /** Tiro, bomba ou míssil, como a sala o disse. */
        readonly kind: 'shot' | 'bomb' | 'missile';
        /** A escala física congelada no disparo, em basis points (gate V28I.CF.3). */
        readonly scaleBps: number;
        /** Onde um obus cai: o centro do círculo dele. Só um obus o tem (gate V28I.CF.1A). */
        readonly targetX?: number;
        readonly targetY?: number;
        /**
         * **A âncora de um corpo linear**, tal como a sala a disse (gate V28I.BX.1).
         *
         * Já viajava no objeto — a ligação entrega-o inteiro — e passa a estar
         * declarado aqui porque a cena passa a lê-lo: com ela, um corpo linear
         * desenha-se em `base + v x (tique - tique_da_base)` no tique do cursor
         * de apresentação, e avança em cada quadro em vez de em cada fotografia.
         * Ausente, a cena desenha `x` e `y`, como sempre.
         */
        readonly linear?: boolean;
        readonly baseX?: number;
        readonly baseY?: number;
        readonly baseTick?: number;
        readonly vx?: number;
        readonly vy?: number;
      };
      readonly x: number;
      readonly y: number;
    }[];
    /**
     * **As invocações na vista**: o Drone e o Leviatã.
     *
     * Uma entidade própria, e não um casco. O que viaja é o que a cena precisa
     * para desenhar; o tempo que lhe falta não vem, porque isso é informação de
     * decisão de quem a invocou.
     */
    /**
     * **As explosões de carta deste quadro**, desenhadas uma vez e esquecidas.
     *
     * A identidade vem do corpo que rebentou, para quem entrega poder
     * deduplicar; o raio é o MECÂNICO, porque o desenho abre exatamente até onde
     * ele feriu.
     */
    readonly cardBursts: readonly {
      readonly id: number;
      readonly kind: 'f' | 'c';
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
    }[];
    /** As explosões de ÁREA confirmadas: hoje, o kamikaze de um Drone. */
    readonly blasts: readonly {
      /** A semente da variação, composta do tique e do autor. Nunca um sorteio. */
      readonly id: number;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
    }[];
    readonly summons: readonly {
      readonly id: number;
      readonly kind: 'd' | 'l';
      readonly x: number;
      readonly y: number;
      readonly aimX: number;
      readonly aimY: number;
      readonly ownerId: number;
      /** A vida que resta, em basis points do máximo dela. */
      readonly healthBps: number;
      /** A entidade de quem a pilota, ou nada. Só o Leviatã. */
      readonly pilotId: number | null;
      /** O raio do corpo, já resolvido pela vida de quem a invocou. */
      readonly radiusMu: number;
    }[];
    /**
     * **Está o MEU casco protegido pela fronteira**, agora.
     *
     * Um booleano derivado do temporizador autoritativo e nada mais: nem o
     * instante em que acaba, nem quantos segundos faltam. A cena desenha
     * enquanto isto for verdade e apaga no quadro em que deixar de ser — não
     * tem relógio próprio, não conta nada e não decide nada.
     *
     * Só o próprio: o `bs` é dito a uma cadeira e não à área de interesse, e
     * saber que o adversário está protegido é uma coisa que se descobre a
     * disparar contra ele.
     */
    readonly protectedNow: boolean;
    /**
     * **Os cometas na vista**, tal como a autoridade os descreveu.
     *
     * Posicao, escala, que partes restam e o nucleo. Nada sobre joias: o
     * protocolo nao as tem antes da aquisicao, e a cena nao pode desenhar uma
     * diferenca que nao lhe foi dita.
     */
    readonly comets: readonly {
      readonly id: string;
      readonly generation: number;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
      readonly parts: number;
      readonly visualSeed: number;
      readonly mask: number;
      readonly coreExposed: boolean;
      readonly coreHealth: number;
    }[];
    /** O que lhes aconteceu nesta trama. Efeitos, desenhados uma vez. */
    readonly cometEvents: readonly {
      readonly id: string;
      readonly ordinal: number;
      readonly kind: 'impact' | 'fragment' | 'reveal' | 'acquired' | 'full';
      readonly part: number;
      readonly x: number;
      readonly y: number;
    }[];
    /**
     * **Os planetas na vista.** Posicao, escala, placas e nucleos EXPOSTOS.
     *
     * Nada sobre o que esta dentro de uma placa ou de um nucleo por abrir: o
     * protocolo nao o tem, e a cena nao pode desenhar o que nao lhe foi dito.
     */
    readonly planets: readonly {
      readonly id: string;
      readonly generation: number;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
      readonly parts: number;
      readonly visualSeed: number;
      readonly mask: number;
      readonly cores: readonly { readonly index: number; readonly health: number }[];
    }[];
    /** O que lhes aconteceu nesta trama. Efeitos, desenhados uma vez. */
    readonly planetEvents: readonly {
      readonly id: string;
      readonly ordinal: number;
      readonly kind: 'impact' | 'fragment' | 'gold' | 'reveal' | 'acquired' | 'full' | 'rebuilt';
      readonly part: number;
      readonly core: number;
      readonly x: number;
      readonly y: number;
    }[];
    /**
     * **Os mobs na vista**, tal como a autoridade os descreveu.
     *
     * `mapIndex` e o tipo visual: a cena resolve por ele o sprite e a escala,
     * pelo mesmo catalogo que a caca usa. Nao ha aqui quem o mob persegue — a
     * postura diz que ele persegue alguem, a direcao diz para onde, e as duas
     * juntas chegam para o desenhar sem dizer onde esta um casco que ninguem
     * viu.
     */
    readonly mobs: readonly {
      readonly id: string;
      readonly generation: number;
      readonly mapIndex: number;
      readonly x: number;
      readonly y: number;
      readonly dirX: number;
      readonly dirY: number;
      readonly radiusMu: number;
      readonly healthMilli: string;
      readonly maxHealthMilli: string;
      readonly stance: 'idle' | 'chasing' | 'returning';
      readonly poisoned: boolean;
      readonly bleeding: boolean;
      /** As penalidades de uma carta, para a família de sinais as desenhar. */
      readonly cards: OpenWorldCreatureCards;
    }[];
    /** O que lhes aconteceu nesta trama. Efeitos, desenhados uma vez. */
    readonly mobEvents: readonly {
      readonly id: string;
      readonly ordinal: number;
      readonly kind: 'hit' | 'attack' | 'death' | 'spawn';
      readonly x: number;
      readonly y: number;
    }[];
    /**
     * **Os bosses na vista.**
     *
     * `mapIndex` e o tipo visual e `scaleBps` a variacao canonica de tamanho;
     * `role` decide um indicador discreto, e nunca um sprite diferente. Nada
     * sobre o pacote: nem conteudo, nem quantidade, nem um campo para eles.
     */
    readonly bosses: readonly {
      readonly id: string;
      readonly generation: number;
      readonly mapIndex: number;
      readonly role: 'melee' | 'caster';
      readonly scaleBps: number;
      readonly x: number;
      readonly y: number;
      readonly dirX: number;
      readonly dirY: number;
      readonly radiusMu: number;
      readonly healthMilli: string;
      readonly maxHealthMilli: string;
      readonly poisoned: boolean;
      readonly bleeding: boolean;
      /** As penalidades de uma carta, para a família de sinais as desenhar. */
      readonly cards: OpenWorldCreatureCards;
      /**
       * **A ultimate em curso, ou nada** (gate V28I.BG). Só um final a tem.
       *
       * `involved` é a resposta sobre ESTE piloto, e é a única que existe: a
       * lista de envolvidos não atravessa a rede, portanto o desenho não pode
       * dizer a ninguém quem mais vai morrer.
       *
       * O anel é a banda que MAGOA, em milli-unidades, e não uma aproximação:
       * um aviso mais estreito do que o golpe seria uma armadilha, e um mais
       * largo faria fugir de nada.
       */
      readonly ultimate: {
        readonly ability: 'abyssal_collapse' | 'sentence' | 'decree';
        readonly phase: 'telegraph' | 'channel' | 'resolve';
        readonly progressMilli: number;
        /** O progresso da interrupção, em milésimos. Zero sem limiar. */
        readonly interruptMilli: number;
        /** As duas bordas do anel do Decreto. Zero nas outras duas. */
        readonly innerMu: number;
        readonly outerMu: number;
        readonly involved: boolean;
      } | null;
    }[];
    /** O que lhes aconteceu nesta trama. Efeitos, desenhados uma vez. */
    readonly bossEvents: readonly {
      readonly id: string;
      readonly ordinal: number;
      readonly kind:
        | 'hit'
        | 'contact'
        | 'cast'
        | 'impact'
        | 'death'
        | 'spawn'
        | 'restored'
        /** Uma etapa de um golpe físico executou-se (gate V28I.BA). */
        | 'strike'
        /** Uma ultimate começou a anunciar-se (gate V28I.BG). */
        | 'ultimate'
        /** Ela executou-se: quem falhou a mecânica morreu. */
        | 'ultimate_executed'
        /** Ela foi interrompida: os jogadores cumpriram a mecânica. */
        | 'ultimate_interrupted';
      readonly x: number;
      readonly y: number;
      /**
       * **O raio MECÂNICO da área**, em milli-unidades, quando o evento teve
       * uma. Ausente é "não teve", e o desenho fica o que sempre foi.
       *
       * A etapa de uma habilidade com área e o rebentamento de uma bala
       * trazem-no: o desenho abre exatamente até onde o dano chegou.
       */
      readonly radiusMu?: number;
    }[];
    /**
     * **Os golpes físicos em curso** (gate V28I.BA), um por boss corpo a corpo
     * que esteja a atacar.
     *
     * A geometria é a MECÂNICA — a mesma que o servidor usou para decidir quem
     * apanhou — porque o desenho tem de ser o que magoa. A cena não decide
     * acerto nenhum e não recalcula duração nenhuma: `progressMilli` é da
     * autoridade, e o desenho interpola sobre ele.
     */
    readonly bossStrikes: readonly {
      readonly id: string;
      readonly strike: {
        readonly ability: /* Os seis golpes físicos (gate V28I.BA). */
          | 'stinger_lunge'
          | 'circular_judgment'
          | 'ember_roar'
          | 'chain_whirl'
          | 'twin_chains'
          | 'reaping_fan'
          /* As seis magias exclusivas dos conjuradores 1 a 6 (gate V28I.BC). */
          | 'crown_of_ashes'
          | 'blood_seal'
          | 'putrid_breath'
          | 'ocular_beam'
          | 'swarm_stance'
          | 'cross_of_lament'
          /* As cinco magias dos conjuradores 7, 8, 10, 14 e 17 (gate V28I.BE). */
          | 'hunger'
          | 'choir_of_faces'
          | 'rotary_fire'
          | 'five_maws'
          | 'hypnotic_dust'
          /* Os três golpes de pressão dos finais (gate V28I.BG). */
          | 'depth_bombardment'
          | 'choral_volley'
          | 'pack_bite';
        readonly phase: 'windup' | 'telegraph' | 'active' | 'recovery';
        readonly progressMilli: number;
        readonly stage: number;
        readonly shape: 'capsule' | 'ring' | 'disc' | 'sector' | 'cross' | 'wave';
        /** O raio INTERIOR de uma onda. Zero nas outras formas. */
        readonly innerMu: number;
        readonly originX: number;
        readonly originY: number;
        readonly dirX: number;
        readonly dirY: number;
        /**
         * **Os pontos congelados, em pares.** `P1` e `P2` das Duas Correntes,
         * os quatro círculos do Bombardeio, ou os sete fins da Salva Coral.
         * Vazio nas outras habilidades.
         */
        readonly points: readonly number[];
        readonly reachMu: number;
        readonly halfWidthMu: number;
        /** O meio-ângulo em basis points da tangente. Zero fora de um sector. */
        readonly sectorTanBps: number;
      } | null;
    }[];
    /**
     * **Os teleguiados dos finais no ar** (gate V28I.BG).
     *
     * Um corpo discreto, como tudo o resto que voa em KENOCYBER: sem feixe, sem
     * linha e sem raio. **Nem o dano nem o alvo viajam** — de quem ele anda
     * atrás vê-se na perseguição, e não há nada a decidir sobre uma coisa que
     * não se pode travar.
     */
    readonly bossHoming: readonly {
      readonly id: number;
      /** A entidade que ele persegue, para o desenho o poder inclinar. */
      readonly targetEntityId: number;
      readonly ability: 'abyssal_harpoon' | 'judgment_spear' | 'crown_gaze';
      readonly x: number;
      readonly y: number;
      readonly dirX: number;
      readonly dirY: number;
      readonly radiusMu: number;
    }[];
    /**
     * **Os núcleos rúnicos da Sentença** (gate V28I.BG).
     *
     * Coisas com vida que se podem partir, e que não pagam nada: sem Ouro, sem
     * XP, sem item e sem crédito de último golpe. A barra é a vida que vem.
     */
    readonly bossCores: readonly {
      readonly id: number;
      readonly bossId: string;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
      readonly healthMilli: string;
      readonly maxHealthMilli: string;
    }[];
    /**
     * **As brasas no chão**, deixadas por um Rugido de Brasa.
     *
     * Os dois relógios são os da SALA. O que resta mede-se contra `tick`, e
     * nunca contra o relógio do navegador.
     */
    readonly bossEmbers: readonly {
      readonly id: number;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
      /** `disc` (uma brasa) ou `sector` (a névoa pútrida). */
      readonly shape: 'disc' | 'sector';
      readonly dirX: number;
      readonly dirY: number;
      readonly sectorTanBps: number;
      /** A lentidão que ela impõe, em basis points. Zero é "não abranda". */
      readonly slowBps: number;
      readonly bornAtTick: number;
      readonly expiresAtTick: number;
    }[];
    /** O tique autoritativo desta trama. O relógio contra o qual tudo se mede. */
    readonly tick: number;
    /**
     * **Quando esta trama chegou**, no relógio de apresentação da página
     * (gate V28I.BX.2). Carimbado pela ligação no instante em que o socket a
     * entregou, e não quando React a empurrou: o cursor mede o intervalo real
     * entre fotografias, sem uma renderização pelo meio.
     */
    readonly receivedAt?: number;
    /**
     * **O instante local a que o tique desta trama corresponde, para o próprio
     * casco**, no mesmo relógio; `null` antes do primeiro reconhecimento.
     */
    readonly ownSyncAt?: number | null;
    /**
     * **As áreas aquáticas na vista.**
     *
     * Um lugar: posição, raio e uma semente decorativa determinística. Nada
     * sobre a população, e não há campo onde ela coubesse.
     */
    readonly waters: readonly {
      readonly id: string;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
      readonly visualSeed: number;
    }[];
    /** Quem está a pescar: a entidade, a água e a fase. Nada sobre o peixe. */
    readonly fishers: readonly {
      readonly entityId: number;
      readonly areaId: string;
      readonly phase: 'waiting' | 'fighting';
      readonly x: number;
      readonly y: number;
    }[];
    /**
     * **A minha pescaria**, para a cena desenhar a linha e a tensão.
     *
     * Só o que o desenho precisa: a fase e a fração de vida que resta. O nome, a
     * raridade e o peso são do painel de React, e não do Phaser.
     */
    readonly fishing: {
      readonly areaId: string;
      readonly phase: 'waiting' | 'fighting';
      /** Milésimos da vida que restam, ou `null` antes da mordida. */
      readonly healthPermille: number | null;
    } | null;
    /**
     * **O comerciante deste porto**, ou nada.
     *
     * Um marco público: posição e o raio dentro do qual se negoceia. A cena
     * desenha-o e desenha o alcance; ela não decide se o botão existe, porque
     * isso é uma resposta do servidor que vive no React.
     */
    readonly merchant: {
      readonly x: number;
      readonly y: number;
      readonly r: number;
    } | null;
    /**
     * **As caixas de morte na vista.**
     *
     * Marcas públicas: onde está, e quanto tempo lhe resta. O conteúdo não vem —
     * ele tem uma trama própria que vai só a quem abriu a caixa, e a cena não
     * precisa dele para desenhar um baú.
     */
    readonly boxes: readonly {
      readonly id: string;
      readonly x: number;
      readonly y: number;
      readonly ttl: number;
    }[];
    /** A caixa que o servidor disse que EU posso abrir. Destaca-se. */
    readonly nearestBox: string | null;
  };
}

export interface HuntSnapshotMessage {
  readonly snapshot: OverviewSnapshot;
  readonly fps: number;
  /**
   * DEV ONLY - whether the cadence boost is on.
   *
   * A boost to HOW OFTEN the ship fires, never to how a shot is drawn: every
   * one of them stays a discrete projectile (see `CLAUDE.md`).
   */
  readonly rapidFire: boolean;
  /**
   * Simulated time of the local run, ms. React reports it at checkpoint as an
   * UPPER BOUND on what to credit - the server still decides.
   */
  readonly tickMs: number;
  /**
   * The deterministic state at exactly `tickMs`, for the checkpoint digest.
   *
   * It is hashed, never uploaded: the server compares its own hash of its own
   * re-simulation and only answers with state when the two disagree.
   */
  readonly deterministicSnapshot: HuntSnapshot;
}

/** Phaser -> React. */
export interface GameToUiMessages {
  /** The scene finished booting and is rendering. */
  'game:ready': { rendererWidth: number; rendererHeight: number };
  /** Periodic overview refresh (throttled; never per-frame). */
  'hunt:snapshot': HuntSnapshotMessage;
  /** The ship died; React shows the death screen. */
  'hunt:death': { stats: DeathStats; cause: 'boss-breach' | 'mob-leaks' };
  /** The hunt restarted after death. */
  'hunt:restarted': Record<string, never>;
  /**
   * The scene could not adopt an authorised session.
   *
   * It exists so that failure is never silent: without it, an exception while
   * swapping sessions travelled back through the bridge into whichever fetch
   * handler had published the message, was reported as a network problem, and
   * left the player looking at an arena that had stopped for no stated reason.
   */
  'hunt:scene-error': {
    /**
     * `art` diz que uma FOLHA de mapa foi recusada (gate V28I.BN).
     *
     * Um índice sem alienígena ou sem chefe — fora de 1 a 20, ou reservado para
     * depois do alfa — não desenha a criatura de outro mapa no lugar: a cena
     * recusa e diz por aqui, uma vez por índice. Os outros três continuam a
     * significar exactamente o que significavam.
     */
    stage: 'adopt-session' | 'adopt-views' | 'reconfigure' | 'art';
    message: string;
  };
  /**
   * The pet fired, with the muzzles the SIMULATION used.
   *
   * Published so the 3D overlay can kick the model back and answer with the
   * positions of its own gun mouths (`hunt:pet-muzzles`). In 2D nothing
   * listens and the scene has already drawn the flashes itself.
   */
  'hunt:pet-volley': { origins: readonly { x: number; y: number }[] };
  /**
   * Where the pet is RIGHT NOW, published once per rendered frame in 3D mode.
   *
   * Presentation, and the only per-frame message on this bus. It exists because
   * `hunt:snapshot` is throttled to 250 ms by design - it carries the overview
   * and the checkpoint digest, and neither wants to be rebuilt sixty times a
   * second - and a renderer fed at 4 Hz draws a staircase. Three numbers,
   * straight from the simulation, never modified on the way and never sent
   * anywhere but the overlay.
   */
  'hunt:pet-transform': {
    pet: { x: number; y: number; facing: number } | null;
    /**
     * Which face a pet with one should wear. Presentation, decided by the
     * scene from its own authoritative state - no request, nothing stored.
     */
    mood: PetMood;
    /**
     * True while the Void Butterfly's beam is firing, and false for every
     * other pet in the game.
     *
     * Presentation only: the model tightens its wingbeat, braces its hinges
     * and brightens the sapphire. Whether the beam is ON is authoritative
     * simulation state - the scene reads it from `getPetBeam()` and forwards
     * it, rather than the overlay guessing from what it can see.
     */
    beaming: boolean;
    /**
     * Where a continuous beam ENDS, in arena coordinates, or null.
     *
     * The beam is drawn by the 3D overlay rather than by the arena, and that is
     * a compositing decision rather than a stylistic one: the pet's canvas sits
     * ABOVE the Phaser canvas, so anything the arena draws inside the pet's
     * silhouette is painted over. A laser leaving a crystal at her chest was
     * buried under her own wings for its first eighty units and reappeared
     * through a gap in the plating - which reads exactly like a beam coming out
     * of her side.
     */
    beamTarget: { x: number; y: number } | null;
  };

  /* --- Duels. Intent out, and failure out. Nothing else. --- */

  /**
   * What the player is doing, coalesced to the protocol's cadence.
   *
   * NOT one message per key event and not one per frame. A browser produces
   * pointer moves far faster than 20 Hz and the room drops anything past its
   * per-second allowance, so the scene reduces raw events to one intent and
   * publishes it at a bounded rate.
   *
   * It is INTENT, and the schema on the wire has no room for anything else:
   * which way I am holding, where I am pointing, whether I am holding fire.
   * There is no position, no health, no damage and no hit here, and the
   * sequence number is not the scene's to invent either - the connection owns
   * it and starts it at the server's `ack + 1`.
   *
   * A cleared intent is the ZERO intent: axes at zero and fire released. That
   * is what blur, a hidden tab, a lost socket and an ending all publish, so a
   * key held when the window went away cannot leave a ship flying.
   */
  'pvp:intent': { readonly intent: PvpIntent };
  /**
   * **Há uma estrutura encostada em que se pode entrar** (gate V28H.X).
   *
   * Publicada pela CENA porque é ela que tem as posições desenhadas e o raio de
   * entrada; o HUD só precisa de saber se mostra o botão. Enviada apenas quando
   * a resposta MUDA - um campo booleano publicado vinte vezes por segundo seria
   * a ponte a pagar por silêncio.
   *
   * Ela não concede nada: quem decide se o embarque acontece é a sala, e o
   * botão só manda a ordem.
   */
  'pvp:board-offer': { readonly available: boolean };
  /**
   * The duel view could not do what it was told.
   *
   * Exists for the same reason `hunt:scene-error` does: without it, an
   * exception while adopting authoritative state travels back through the
   * bridge into whichever handler published the message, gets reported as
   * something else entirely, and leaves the player looking at an arena that
   * stopped for no stated reason.
   */
  /**
   * The duel scene has been created and is listening.
   *
   * Sent once per scene, from `create`. It exists because the React feature
   * mounts BEFORE Phaser boots - it wraps the host that builds it - so a
   * `pvp:open` published from a mount effect had no subscriber and was lost.
   * A reloaded page therefore rejoined the room, showed the HUD, and never
   * opened the arena behind it.
   */
  'pvp:scene-ready': Record<string, never>;
  /**
   * **O comando deste instante**, lido pela cena e enviado por React.
   *
   * A cena é dona do teclado e do rato porque é ela que tem a câmara e a
   * posição desenhada; o socket é de React porque é ele que tem o bilhete e a
   * travessia. Um por frame, sem fila.
   */
  'open-world:command': {
    readonly mx: -1 | 0 | 1;
    readonly my: -1 | 0 | 1;
    readonly aimX: number;
    readonly aimY: number;
    readonly firing: boolean;
  };
  /** A cena não conseguiu montar ou desenhar. Uma arena parada em silêncio não serve. */
  /**
   * **Há uma estrutura vazia ao alcance?** (gate V28I.P).
   *
   * A CENA responde — é ela que tem as posições desenhadas e o raio de entrada —
   * e publica só quando a resposta muda. A sala REVALIDA no tique em que a
   * ordem chega: o botão é um convite, e nunca uma decisão.
   */
  'open-world:board-offer': { readonly available: boolean };
  /**
   * **A cena do mundo aberto foi criada e está a ouvir** (gate V28I.BZ).
   *
   * Enviada uma vez por cena, no fim do `create`, pela mesma razão que
   * `pvp:scene-ready`: depois de um F5 a meio de uma expedição o React sabe que
   * a tem de restaurar antes de o Phaser arrancar, e um `open-world:open`
   * publicado antes de a cena subscrever não chegava a ninguém.
   */
  'open-world:scene-ready': Record<string, never>;
  'open-world:scene-error': {
    readonly stage: string;
    readonly message: string;
  };
  'pvp:scene-error': {
    /**
     * `update` is the one that can arrive while a duel is being played.
     *
     * The other four happen at a boundary the player can see - opening,
     * adopting a world, closing, loading art. A frame that throws leaves Phaser
     * drawing the last display list it had, so the arena freezes with
     * everything still on it and nothing says why. This is what says why.
     */
    readonly stage: 'open' | 'adopt' | 'close' | 'art' | 'update';
    readonly message: string;
  };
}

type Handler<T> = (payload: T) => void;

export interface GameBridge {
  sendToGame<K extends keyof UiToGameMessages>(type: K, payload: UiToGameMessages[K]): void;
  sendToUi<K extends keyof GameToUiMessages>(type: K, payload: GameToUiMessages[K]): void;
  onGameMessage<K extends keyof UiToGameMessages>(
    type: K,
    handler: Handler<UiToGameMessages[K]>,
  ): () => void;
  onUiMessage<K extends keyof GameToUiMessages>(
    type: K,
    handler: Handler<GameToUiMessages[K]>,
  ): () => void;
}

/** Plain synchronous pub/sub. No Phaser or React types leak through. */
export function createGameBridge(): GameBridge {
  const toGame = new Map<string, Set<Handler<never>>>();
  const toUi = new Map<string, Set<Handler<never>>>();

  function subscribe<T>(
    registry: Map<string, Set<Handler<never>>>,
    type: string,
    handler: Handler<T>,
  ) {
    let handlers = registry.get(type);
    if (!handlers) {
      handlers = new Set();
      registry.set(type, handlers);
    }
    handlers.add(handler as Handler<never>);
    return () => {
      handlers.delete(handler as Handler<never>);
    };
  }

  /**
   * Delivers to every subscriber, and lets none of them stop the others.
   *
   * A message is a broadcast, not a call: the sender is telling the other side
   * something happened and has no stake in what any listener does about it. An
   * uncaught throw here propagated all the way back into a React effect and
   * unmounted the application - one stale scene, and the whole page went black
   * with no message on it. Isolating the handler keeps the blast radius at the
   * handler, and the failure is still LOUD: it goes to the console with the
   * message type on it rather than being quietly dropped.
   *
   * This is not a licence for handlers to throw. The scene that did it is
   * fixed; this is what makes the next one a bug report instead of an outage.
   */
  function publish<T>(registry: Map<string, Set<Handler<never>>>, type: string, payload: T) {
    const handlers = registry.get(type);
    if (!handlers) {
      return;
    }
    // A copy: a handler is allowed to unsubscribe itself, and mutating the set
    // being iterated is how "some listeners silently stopped getting messages"
    // starts.
    for (const handler of [...handlers]) {
      try {
        (handler as Handler<T>)(payload);
      } catch (error) {
        console.error(`bridge handler for "${type}" threw`, error);
      }
    }
  }

  return {
    sendToGame: (type, payload) => publish(toGame, type, payload),
    sendToUi: (type, payload) => publish(toUi, type, payload),
    onGameMessage: (type, handler) => subscribe(toGame, type, handler),
    onUiMessage: (type, handler) => subscribe(toUi, type, handler),
  };
}
